import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import ChecklistSection from './ChecklistSection';
import PrepSummary from './PrepSummary';
import RecommendedActionCallout from './RecommendedActionCallout';
import ShortageSubform from './ShortageSubform';
import Input from '../../../components/atoms/forms/Input';
import SingleSelect from '../../../components/atoms/forms/SingleSelect';
import useChecklistSection from './useChecklistSection';
import useSupervisorAction from './useSupervisorAction';
import { toReviewPhase } from './utils';

export const PHASE_PREP = 'prep';
export const PHASE_DAY_OF = 'day-of';

// Separate localStorage keys per phase so the Prep snapshot (the
// supervisor's day-before knowledge: "we'll be one driver short") doesn't
// get overwritten by the Day Of confirmation at execution time. Both
// snapshots are valuable for audit and route adjustments.
function sectionIdFor(phase) {
  return phase === PHASE_PREP ? 'headcount-prep' : 'headcount-day-of';
}

// SingleSelect renders the empty/placeholder via its `placeholder` prop, so
// the option arrays carry no empty leading entry.
const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes — all available' },
  { value: 'no', label: 'No — I have a different number' },
];

const DRIVER_REASONS = [
  { value: 'call_off', label: 'Call-Off' },
  { value: 'no_show', label: 'No-Show' },
  { value: 'sick', label: 'Sick' },
  { value: 'pto', label: 'PTO' },
  { value: 'injury', label: 'Injury' },
  { value: 'quit', label: 'Quit' },
  { value: 'other', label: 'Other' },
];

const VAN_REASONS = [
  { value: 'broke_down', label: 'Broke down (mechanical)' },
  { value: 'in_use_other_route', label: 'Still in use on different route' },
  { value: 'maintenance', label: 'In scheduled maintenance' },
  { value: 'damaged', label: 'Damaged / out of service' },
  { value: 'refrigeration_issue', label: 'Refrigeration issue' },
  { value: 'accident', label: 'Accident / collision damage' },
  { value: 'other', label: 'Other' },
];

const INITIAL_FIELDS = {
  driversAvailable: '',
  actualDriverCount: '',
  driverReason: '',
  driverUnavailableRoutes: [],
  trainingCount: '',
  leadsCount: '',
  vansAvailable: '',
  actualVanCount: '',
  vanReason: '',
  vanUnavailableRoutes: [],
};

const StyledDiv = styledComponent('div', {
  '& .hc-field': {
    marginBottom: theme.spacing.fieldGap,
  },
  '& .hc-label': {
    display: 'block',
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .hc-ride-along-grid': {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing.fieldGap,
  },
  '& .hc-ride-along-note': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.labelGap,
  },
});

// Ride-along headcounts (Training / Leads) are informational only — they
// travel with the driver and don't affect route coverage or completion.
// Blank means "not reported", so it's omitted rather than sent as 0.
function rideAlongCount(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

// Builds the BE-expected DriverHeadcount data shape from the section's
// draft fields. `actualCount` defaults to plannedCount when the
// supervisor reports all drivers present; otherwise the typed-in value.
function buildDriverHeadcountData(fields, routesPlanned) {
  const allPresent = fields.driversAvailable === 'yes';
  return {
    plannedCount: routesPlanned,
    allDriversPresent: allPresent,
    actualCount: allPresent
      ? routesPlanned
      : Number(fields.actualDriverCount) || 0,
    reasonCode: fields.driverReason || undefined,
    notes: (fields.driverUnavailableRoutes || []).join(', '),
    trainingCount: rideAlongCount(fields.trainingCount),
    leadsCount: rideAlongCount(fields.leadsCount),
  };
}

function buildVanHeadcountData(fields, routesPlanned) {
  const allAvailable = fields.vansAvailable === 'yes';
  return {
    plannedCount: routesPlanned,
    allVansAvailable: allAvailable,
    actualCount: allAvailable
      ? routesPlanned
      : Number(fields.actualVanCount) || 0,
    reasonCode: fields.vanReason || undefined,
    notes: (fields.vanUnavailableRoutes || []).join(', '),
  };
}

// Inverse of buildDriverHeadcountData. `notes` carries the joined unavailable
// route names. actualCount is stringified for the controlled number input.
export function parseDriverHeadcountData(data) {
  if (!data || Object.keys(data).length === 0) return null;
  return {
    driversAvailable: data.allDriversPresent ? 'yes' : 'no',
    actualDriverCount:
      data.actualCount === undefined || data.actualCount === null
        ? ''
        : String(data.actualCount),
    driverReason: data.reasonCode || '',
    driverUnavailableRoutes: data.notes ? data.notes.split(', ') : [],
    trainingCount:
      data.trainingCount === undefined || data.trainingCount === null
        ? ''
        : String(data.trainingCount),
    leadsCount:
      data.leadsCount === undefined || data.leadsCount === null
        ? ''
        : String(data.leadsCount),
  };
}

export function parseVanHeadcountData(data) {
  if (!data || Object.keys(data).length === 0) return null;
  return {
    vansAvailable: data.allVansAvailable ? 'yes' : 'no',
    actualVanCount:
      data.actualCount === undefined || data.actualCount === null
        ? ''
        : String(data.actualCount),
    vanReason: data.reasonCode || '',
    vanUnavailableRoutes: data.notes ? data.notes.split(', ') : [],
  };
}

function isShortageComplete(answer, actualCount, reason) {
  if (answer === '') return false;
  if (answer === 'yes') return true;
  // answer === 'no' → both actual count and reason required
  return (
    actualCount !== '' &&
    actualCount !== null &&
    Number(actualCount) >= 0 &&
    reason !== ''
  );
}

function HeadcountSection({
  phase,
  stockingDate,
  fulfillmentCenter,
  market,
  routesPlanned,
  stopsCount,
  routeNames,
  stepNumber,
  disabled,
  disabledTooltip,
  serverFields,
  hasServerAction,
  showConsolidateCta,
  onConsolidateRoutes,
  onCompletedChange,
}) {
  const { fields, setField, markComplete, cancel, completed } =
    useChecklistSection({
      sectionId: sectionIdFor(phase),
      stockingDate,
      initialFields: INITIAL_FIELDS,
      serverFields,
      hasServerAction,
      onCompletedChange,
    });

  const trainingCountId = 'hc-training-count';
  const leadsCountId = 'hc-leads-count';

  const reviewPhase = toReviewPhase(phase);
  const supervisorAction = useSupervisorAction({
    fulfillmentCenter,
    market,
    reviewDate: stockingDate,
    reviewPhase,
  });

  const handleMarkComplete = useCallback(() => {
    markComplete();
    supervisorAction.submit({
      eventType: 'DriverHeadcount',
      data: buildDriverHeadcountData(fields, routesPlanned),
    });
    supervisorAction.submit({
      eventType: 'VanHeadcount',
      data: buildVanHeadcountData(fields, routesPlanned),
    });
  }, [markComplete, supervisorAction, fields, routesPlanned]);

  const driversComplete = isShortageComplete(
    fields.driversAvailable,
    fields.actualDriverCount,
    fields.driverReason,
  );
  const vansComplete = isShortageComplete(
    fields.vansAvailable,
    fields.actualVanCount,
    fields.vanReason,
  );
  const markCompleteDisabled = !(driversComplete && vansComplete);

  const showCTA =
    fields.driversAvailable === 'no' || fields.vansAvailable === 'no';

  const plannedDisplay = useMemo(() => {
    if (typeof routesPlanned !== 'number' || routesPlanned <= 0) return '—';
    return String(routesPlanned);
  }, [routesPlanned]);

  // Tile inputs: needs are computed from the plan — 1 driver + 1 van per
  // route. null (rendered as an em-dash) until the plan is known.
  const plannedCount =
    typeof routesPlanned === 'number' && routesPlanned > 0
      ? routesPlanned
      : null;
  const summaryTitle =
    phase === PHASE_PREP
      ? 'Tomorrow’s plan — does this look right?'
      : 'Today’s plan — does this look right?';

  // react-select wants {value,label}; we persist arrays of names so the draft
  // value is portable. Convert in both directions here.
  const routeOptions = useMemo(
    () => routeNames.map((name) => ({ value: name, label: name })),
    [routeNames],
  );
  const handleConsolidateClick = () => {
    // Surface the union of driver + van missing routes so the parent can
    // pre-check those routes' consolidation checkboxes before opening the
    // review modal.
    const unavailableRoutes = Array.from(
      new Set([
        ...(fields.driverUnavailableRoutes || []),
        ...(fields.vanUnavailableRoutes || []),
      ]),
    );
    onConsolidateRoutes({ unavailableRoutes });
  };

  return (
    <StyledDiv data-testid="headcount">
      <ChecklistSection
        stepNumber={stepNumber}
        title="Driver &amp; Van Headcount"
        description="Confirm available drivers and vans for the selected date."
        completed={completed}
        disabled={disabled}
        disabledTooltip={disabledTooltip}
        onCancel={cancel}
        onMarkComplete={handleMarkComplete}
        markCompleteDisabled={markCompleteDisabled}
      >
        <PrepSummary
          title={summaryTitle}
          routesCount={plannedCount}
          stopsCount={typeof stopsCount === 'number' ? stopsCount : null}
          driversNeeded={plannedCount}
          vansNeeded={plannedCount}
        />

        <div className="hc-field">
          <span className="hc-label">
            Do you have all {plannedDisplay} drivers?
          </span>
          <SingleSelect
            inputId="hc-drivers-available"
            options={YES_NO_OPTIONS}
            value={
              YES_NO_OPTIONS.find((o) => o.value === fields.driversAvailable) ||
              null
            }
            onChange={(opt) =>
              setField('driversAvailable', opt ? opt.value : '')
            }
            placeholder="Select…"
            isClearable
            data-testid="hc-drivers-available"
          />
        </div>

        <div className="hc-field">
          <div className="hc-ride-along-grid">
            <div>
              <label className="hc-label" htmlFor={trainingCountId}>
                Training
              </label>
              <Input
                id={trainingCountId}
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={fields.trainingCount}
                onChange={(e) => setField('trainingCount', e.target.value)}
                dataTestId="hc-training-count"
              />
            </div>
            <div>
              <label className="hc-label" htmlFor={leadsCountId}>
                Leads
              </label>
              <Input
                id={leadsCountId}
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={fields.leadsCount}
                onChange={(e) => setField('leadsCount', e.target.value)}
                dataTestId="hc-leads-count"
              />
            </div>
          </div>
          <p className="hc-ride-along-note">
            Training and leads ride along with the driver — only people
            physically driving a van count as drivers.
          </p>
        </div>

        {fields.driversAvailable === 'no' && (
          <ShortageSubform
            kind="driver"
            count={fields.actualDriverCount}
            onCountChange={(v) => setField('actualDriverCount', v)}
            reason={fields.driverReason}
            onReasonChange={(v) => setField('driverReason', v)}
            reasonOptions={DRIVER_REASONS}
            routes={fields.driverUnavailableRoutes}
            onRoutesChange={(names) =>
              setField('driverUnavailableRoutes', names)
            }
            routeOptions={routeOptions}
          />
        )}

        <div className="hc-field">
          <span className="hc-label">
            Do you have all {plannedDisplay} vans?
          </span>
          <SingleSelect
            inputId="hc-vans-available"
            options={YES_NO_OPTIONS}
            value={
              YES_NO_OPTIONS.find((o) => o.value === fields.vansAvailable) ||
              null
            }
            onChange={(opt) => setField('vansAvailable', opt ? opt.value : '')}
            placeholder="Select…"
            isClearable
            data-testid="hc-vans-available"
          />
        </div>

        {fields.vansAvailable === 'no' && (
          <ShortageSubform
            kind="van"
            count={fields.actualVanCount}
            onCountChange={(v) => setField('actualVanCount', v)}
            reason={fields.vanReason}
            onReasonChange={(v) => setField('vanReason', v)}
            reasonOptions={VAN_REASONS}
            routes={fields.vanUnavailableRoutes}
            onRoutesChange={(names) => setField('vanUnavailableRoutes', names)}
            routeOptions={routeOptions}
          />
        )}

        {showCTA && showConsolidateCta && (
          <RecommendedActionCallout
            title="Recommended action"
            body="Consolidate the route(s) without a driver or van. NextMV
              will redistribute their stops across the remaining vehicles."
            ctaLabel="Consolidate Routes →"
            onCta={handleConsolidateClick}
            testId="hc-action"
            ctaTestId="hc-route-editor-cta"
          />
        )}
      </ChecklistSection>
    </StyledDiv>
  );
}

HeadcountSection.propTypes = {
  phase: PropTypes.oneOf([PHASE_PREP, PHASE_DAY_OF]).isRequired,
  stockingDate: PropTypes.string.isRequired,
  fulfillmentCenter: PropTypes.string.isRequired,
  market: PropTypes.string,
  routesPlanned: PropTypes.number,
  stopsCount: PropTypes.number,
  routeNames: PropTypes.arrayOf(PropTypes.string),
  stepNumber: PropTypes.number,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
  serverFields: PropTypes.shape({
    driversAvailable: PropTypes.string,
    actualDriverCount: PropTypes.string,
    driverReason: PropTypes.string,
    driverUnavailableRoutes: PropTypes.arrayOf(PropTypes.string),
    trainingCount: PropTypes.string,
    leadsCount: PropTypes.string,
    vansAvailable: PropTypes.string,
    actualVanCount: PropTypes.string,
    vanReason: PropTypes.string,
    vanUnavailableRoutes: PropTypes.arrayOf(PropTypes.string),
  }),
  hasServerAction: PropTypes.bool,
  showConsolidateCta: PropTypes.bool,
  onConsolidateRoutes: PropTypes.func,
  onCompletedChange: PropTypes.func.isRequired,
};

HeadcountSection.defaultProps = {
  market: '',
  routesPlanned: 0,
  stopsCount: null,
  routeNames: [],
  stepNumber: 2,
  disabled: false,
  disabledTooltip: '',
  serverFields: null,
  hasServerAction: false,
  showConsolidateCta: true,
  onConsolidateRoutes: () => {},
};

export default HeadcountSection;
